import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { addMinutes, isBefore } from 'date-fns';

import PrivateRoute from 'components/Navigation/ProtectedRoute';
import { showErrorToast, showSuccessToast } from 'components/Toast';
import SolidButton from 'components/SolidButton';
import Modal from 'components/Modal';
import Table from 'components/Table';

import { IOperation } from 'utils/interfaces';
import { formatDate } from 'utils/functions';
import { BUTTON_COLORS } from 'utils/enums';
import GeneralApi from 'utils/generalApi';
import { useAuth } from 'contexts/Auth';
import {
  ADMIN_OPERATIONS,
  CARDS,
  OPERATIONS_AUX,
  OPERATION_STATUS,
  PAY_MODES,
  PROOF_KINDS,
  TO_ACCOUNTS,
} from 'utils/constants';

export default function OperationsPage() {
  const history = useNavigate();
  const auth = useAuth();

  const generalApi = useMemo(() => new GeneralApi(auth, history), [auth, history]);

  const [refresh, setRefresh] = useState(true);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalDocs, setTotalDocs] = useState(0);

  const [modalLoading, setModalLoading] = useState(false);
  const [operations, setOperations] = useState<Array<IOperation>>([]);

  const [actionData, setActionData] = useState<
    | {
        action: 'reactivate' | 'delete' | 'pay' | 'reject' | 'sign';
        text: string;
        data: IOperation;
      }
    | undefined
  >(undefined);

  const actionResult = async () => {
    if (!actionData) return;
    const requestData = {
      url: '',
      toast: '',
      data: {
        operationId: actionData.data._id,
        status: '',
      },
    };
    switch (actionData.action) {
      case 'reactivate':
        requestData.url = '/reactivate_operation';
        requestData.toast = 'Operación reactivada';
        break;
      case 'delete':
        requestData.url = '/remove_operation';
        requestData.toast = 'Operación eliminada';
        break;
      case 'pay':
        requestData.url = '/pay_operation';
        requestData.toast = 'Operación aprobada';
        requestData.data.status = 'PAID';
        break;
      case 'reject':
        requestData.url = '/pay_operation';
        requestData.toast = 'Operación rechazada';
        requestData.data.status = 'REJECTED';
        break;
      case 'sign':
        requestData.url = '/sign_operation';
        requestData.toast = 'Operación firmada';
        break;

      default:
        return;
    }
    setModalLoading(true);
    let result: any = await generalApi.post(requestData.url, requestData.data);
    setModalLoading(false);
    if (!result.success) return showErrorToast(result.data.err);
    showSuccessToast(requestData.toast);
    setRefresh(true);
    setActionData(undefined);
  };

  useEffect(() => {
    const getOperations = async () => {
      setLoading(true);
      const result = await generalApi.get(`/pending_operations?page=${currentPage}&limit=10`);
      if (!result.success) {
        showErrorToast(result.message);
        setLoading(false);
        return;
      }
      setOperations(result.data.docs);
      setTotalDocs(result.data.totalDocs);
      setLoading(false);
    };
    if (refresh) {
      getOperations();
      setRefresh(false);
    }
  }, [generalApi, refresh, currentPage]);

  return (
    <PrivateRoute>
      <div className="text-[#1B205A]">
        <Modal
          isOpen={actionData !== undefined}
          onClose={() => {
            setActionData(undefined);
          }}
        >
          <div className="rounded-t-lg bg-primary-600 px-4 pb-4 sm:flex sm:items-start sm:p-6 sm:py-4">
            <div className="mt-3 text-center sm:mt-0 sm:text-left">
              <h3 className="text-lg font-medium leading-6 text-white" id="modal-headline">
                Confirmar acción
              </h3>
            </div>
          </div>
          <div className="px-4 py-5 sm:p-6">
            <p>{actionData?.text}</p>
          </div>
          <div className="rounded-b-lg bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
            <SolidButton
              className="w-auto sm:ml-3"
              isLoading={modalLoading}
              onClick={() => actionResult()}
            >
              Confirmar
            </SolidButton>
            <button
              type="button"
              onClick={() => setActionData(undefined)}
              className="focus:ring-primary-500 mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
            >
              Cancelar
            </button>
          </div>
        </Modal>
        <div className="pb-8 2xl:px-24">
          <div className="mt-6 justify-between space-y-4 px-8 pb-4 sm:flex sm:space-y-0">
            <p className="text-2xl font-bold">Lista de Operaciones</p>
          </div>
          <Table
            isLoading={loading}
            loadingText="Buscando operaciones"
            initialSort={{
              key: 'created_at',
              order: 'desc',
            }}
            currentPage={currentPage}
            totalDocs={totalDocs}
            onPageChange={(page) => {
              setCurrentPage(page);
              setRefresh(true);
            }}
            columns={[
              {
                title: 'ESTADO',
                dataKey: 'status',
                sortValue: (item: IOperation) => OPERATION_STATUS[item.status],
                render: (item: IOperation) => OPERATION_STATUS[item.status],
              },
              {
                title: 'CÓDIGO',
                dataKey: 'code',
              },
              {
                title: 'FECHA',
                dataKey: 'created_at',
                render: (item: IOperation) =>
                  formatDate(new Date(item.created_at), 'yyyy/MM/dd, hh:mm a', false),
              },
              {
                title: 'Clase Ben',
                dataKey: 'to_account',
                render: (item: IOperation) => TO_ACCOUNTS[item.to_account],
              },
              {
                title: 'Cliente',
                key: 'customer',
                dataKey: 'user',
                sortValue: (item: IOperation) =>
                  item.user.level !== '30'
                    ? item.user.company_name
                    : `${item.user.name} ${item.user.lname_p} ${item.user.lname_m}`,
                render: (item: IOperation) =>
                  item.user.level !== '30'
                    ? item.user.company_name
                    : `${item.user.name} ${item.user.lname_p} ${item.user.lname_m}`,
              },
              {
                title: 'Usuario',
                dataKey: 'user',
                sortValue: (item: IOperation) =>
                  item.user.level !== '30'
                    ? `${item.user.name} ${item.user.lname_p} ${item.user.lname_m}`
                    : '-',
                render: (item: IOperation) =>
                  item.user.level !== '30'
                    ? `${item.user.name} ${item.user.lname_p} ${item.user.lname_m}`
                    : '-',
              },
              {
                title: 'Beneficiario',
                dataKey: 'to_name',
                sortValue: (item: IOperation) =>
                  item.user.level !== '30' && item.to_account === 'P'
                    ? item.user.company_name
                    : item.to_name,
                render: (item: IOperation) => (
                  <>
                    <p>
                      {item.user.level !== '30' && item.to_account === 'P' ? 'RUC' : item.to_doc}:{' '}
                      {item.user.level !== '30' && item.to_account === 'P'
                        ? item.user.username.split('|')[1]
                        : item.to_number}
                    </p>
                    <p>
                      {item.user.level !== '30' && item.to_account === 'P'
                        ? item.user.company_name
                        : item.to_name}
                    </p>
                  </>
                ),
              },
              {
                title: 'Importe',
                dataKey: 'amount',
              },
              {
                title: 'TIPO DE OPERACIÓN',
                dataKey: 'action',
                render: (item: IOperation) => ADMIN_OPERATIONS[OPERATIONS_AUX[item.action]],
              },
              {
                title: 'Precio',
                dataKey: 'final_rate',
              },
              {
                title: 'Ingresa',
                dataKey: 'origin_bank',
                sortValue: (item: IOperation) =>
                  item.action === 'C' ? item.amount * item.final_rate : item.amount,
                render: (item: IOperation) =>
                  item.amount && item.final_rate ? (
                    <>
                      <p>
                        {item.action === 'C'
                          ? (item.amount * item.final_rate)
                              ?.toFixed(2)
                              ?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                          : item.amount?.toFixed(2)?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      </p>
                      <p>{item.origin_bank}</p>
                    </>
                  ) : (
                    item.origin_bank
                  ),
              },
              {
                title: 'Sale',
                dataKey: 'destiny_bank',
                sortValue: (item: IOperation) =>
                  item.action === 'C' ? item.amount : item.amount * item.final_rate,
                render: (item: IOperation) =>
                  item.amount && item.final_rate ? (
                    <>
                      <p>
                        {item.action === 'C'
                          ? item.amount?.toFixed(2)?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                          : (item.amount * item.final_rate)
                              ?.toFixed(2)
                              ?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      </p>
                      <p>{item.destiny_bank}</p>
                    </>
                  ) : (
                    item.destiny_bank
                  ),
              },
              {
                title: 'Método de pago',
                dataKey: 'pay_mode',
                render: (item: IOperation) => (
                  <>
                    <p>{PAY_MODES[item.pay_mode]}</p>
                    {item.pay_mode === 'RC' ? (
                      <>
                        <p>{item.to_acc_number}</p>
                        {item.to_cci && <p>CCI: {item.to_cci}</p>}
                      </>
                    ) : item.to_card ? (
                      <>
                        <p>{CARDS[item.to_card]}</p>
                        <p>{item.to_acc_number}</p>
                      </>
                    ) : (
                      '-'
                    )}
                  </>
                ),
              },
              {
                title: 'Prueba',
                dataKey: 'proof',
                render: (item: IOperation) => (
                  <>
                    {item.proof === 'mail' ? (
                      <p>{PROOF_KINDS[item.proof]}</p>
                    ) : item.proof === 'file' ? (
                      <a
                        href={item.proof_URL}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-700 underline hover:text-blue-800"
                      >
                        {PROOF_KINDS[item.proof]}
                      </a>
                    ) : (
                      '-'
                    )}
                  </>
                ),
              },
              {
                title: 'OPCIONES',
                dataKey: 'options',
                disableSort: true,
                render: (item: IOperation) => {
                  let operationDate = addMinutes(
                    new Date(item.reactivated_at || item.created_at),
                    10
                  );
                  return (
                    <div className="flex space-x-2 pr-2">
                      {item.status === '0' && isBefore(operationDate, new Date()) && (
                        <>
                          <SolidButton
                            color={BUTTON_COLORS.BLUE}
                            onClick={() =>
                              setActionData({
                                action: 'reactivate',
                                text: '¿Está seguro que desea reactivar ésta operación?',
                                data: item,
                              })
                            }
                          >
                            Reactivar
                          </SolidButton>
                          <SolidButton
                            color={BUTTON_COLORS.RED}
                            onClick={() =>
                              setActionData({
                                action: 'delete',
                                text: '¿Está seguro que desea eliminar ésta operación?',
                                data: item,
                              })
                            }
                          >
                            Eliminar
                          </SolidButton>
                        </>
                      )}
                      {item.status === '1' && (
                        <>
                          <SolidButton
                            color={BUTTON_COLORS.BLUE}
                            onClick={() =>
                              setActionData({
                                action: 'pay',
                                text: '¿Está seguro que desea pagar ésta operación?',
                                data: item,
                              })
                            }
                          >
                            Pagar
                          </SolidButton>
                          <SolidButton
                            color={BUTTON_COLORS.RED}
                            onClick={() =>
                              setActionData({
                                action: 'reject',
                                text: '¿Está seguro que desea rechazar ésta operación?',
                                data: item,
                              })
                            }
                          >
                            Rechazar
                          </SolidButton>
                        </>
                      )}
                      {item.status === '3' && (
                        <SolidButton
                          color={BUTTON_COLORS.BLUE}
                          onClick={() =>
                            setActionData({
                              action: 'sign',
                              text: '¿Está seguro que desea firmar ésta operación?',
                              data: item,
                            })
                          }
                        >
                          Firmar
                        </SolidButton>
                      )}
                    </div>
                  );
                },
              },
            ]}
            data={operations}
          />
        </div>
      </div>
    </PrivateRoute>
  );
}
